import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import axios from 'src/lib/axios';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

import type { CreateCashCollections } from 'src/features/cashCollections/types/cashCollections';
import type { MutationConfig } from 'src/lib/react-query';
import type { GeneralSmartParkError } from 'src/types';

type FormSchemaValues = z.infer<typeof formSchema>;

export type CreateCashCollectionFnType = typeof createCashCollection;

export const formSchema = z
    .object({
        collected_by: z.string({ message: validationMessagesMap.validationErrorName }).min(3),
        parking: z.string({ message: validationMessagesMap.validationErrorRequired }),
        paystation: z.string({ message: validationMessagesMap.validationErrorRequired }),
        status: z.enum(['paid', 'collected', 'loaded']).optional(),
        nominal_amounts: z.record(
            z.string({ message: validationMessagesMap.validationErrorRequired }),
            z.number({ required_error: validationMessagesMap.validationErrorRequired }).min(0),
        ),
        description: z.string().optional(),
        date: z.string({ message: validationMessagesMap.validationErrorRequired }),
        time: z.string({ message: validationMessagesMap.validationErrorRequired }),
        files: z.array(z.instanceof(File)).optional(),
        type: z.enum(['collect', 'load'], { message: validationMessagesMap.validationErrorRequired }),
    })
    .superRefine((values, ctx) => {
        if (values.type === 'collect' && !values.status) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: validationMessagesMap.validationErrorRequired,
                path: ['status'],
            });
        }
    });

const RESOURCE = '/cash-collection/cash-collection/';

interface UseCreateCashCollection extends MutationConfig<CreateCashCollectionFnType, GeneralSmartParkError> {}

const createCashCollection = (formData: FormData): Promise<CreateCashCollections> => {
    return axios.post(formatResourcePath(RESOURCE), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const useCreateCashCollection = (options: UseCreateCashCollection = {}) =>
    useMutation({
        mutationFn: createCashCollection,
        ...options,
    });
export { formSchema as createCashCollectionFormSchema, RESOURCE as CASH_COLLECTION_RESOURCE };
export type { FormSchemaValues as CreateCashCollectionFormSchemaValues };
